import { useEffect, useState } from 'react';

export type Filters = 'current' | 'all';

const GLOBAL_MODIFICATIONS_KEY = 'Global modification';

export type Modifiable = {
  content: {
    isGlobal?: boolean;
    url: string;
    title?: string;
    pages?: string[];
    name?: string;
  };
};

type ModificationsByUrl<T> = Record<string, T[]>;

const groupByUrl = <T extends Modifiable>(modifications: T[]) => {
  const globalModifications: T[] = [];
  const modificationsByUrl: ModificationsByUrl<T> = modifications.reduce(
    (acc, modification) => {
      if (modification.content?.isGlobal) {
        globalModifications.push(modification);
      } else {
        const url = modification?.content?.pages
          ? modification.content.pages.join(',')
          : modification.content.url;
        acc[url] = acc[url] || [];
        acc[url].push(modification);
      }
      return acc;
    },
    {} as ModificationsByUrl<T>
  );
  return { globalModifications, modificationsByUrl };
};

export const filterModificationsByText = <T extends Modifiable>(
  modifications: T[],
  searchValue: string
) => {
  return modifications.filter((modification) => {
    const stringToCompare = modification.content?.title
      ? modification.content.title
      : modification?.content?.name;
    return stringToCompare.toLowerCase().includes(searchValue.toLowerCase());
  });
};

const filterModifications = <T extends Modifiable>(
  modificationsByFilter: ModificationsByUrl<T>,
  searchValue: string
) => {
  return Object.entries(modificationsByFilter).reduce(
    (acc, [url, modifications = []]) => {
      const filteredModifications = filterModificationsByText(
        modifications,
        searchValue
      );

      if (filteredModifications.length) {
        acc[url] = filteredModifications;
      }
      return acc;
    },
    {} as ModificationsByUrl<T>
  );
};

type UseModificationsFilterParams<T extends Modifiable> = {
  modifications: T[];
  pageUrl: string;
};

export const useModificationsFilter = <T extends Modifiable>({
  modifications,
  pageUrl,
}: UseModificationsFilterParams<T>) => {
  const [selectedFilter, setSelectedFilter] = useState<Filters>('all');
  const [searchValue, setSearchValue] = useState('');

  const { globalModifications, modificationsByUrl } = groupByUrl(modifications);

  const allModificationsByUrl = {
    [GLOBAL_MODIFICATIONS_KEY]: globalModifications ?? [],
    ...modificationsByUrl,
  };

  const modificationsByFilter =
    selectedFilter === 'all'
      ? allModificationsByUrl
      : {
          [GLOBAL_MODIFICATIONS_KEY]: globalModifications,
          [pageUrl]: modifications[0]?.content?.pages
            ? Object.entries(modificationsByUrl).reduce(
                (acc, [url, modifications]) => {
                  // url of modification AI is a string of pages separated by commas
                  if (url.split(',').includes(pageUrl)) {
                    acc[pageUrl] = [...modifications, ...(acc[pageUrl] ?? [])];
                  }
                  return acc;
                },
                {} as ModificationsByUrl<T>
              )?.[pageUrl] ?? []
            : modificationsByUrl[pageUrl],
        };

  const modificationsByFilterAndSearch = filterModifications(
    modificationsByFilter,
    searchValue
  );

  return {
    result: modificationsByFilterAndSearch,
    selectedFilter,
    onFiltersChange: setSelectedFilter,
    searchValue,
    onSearchChange: setSearchValue,
  };
};

// return modification filter for flat view (no grouping by url)
export const useModificationFilterFlat = <T extends Modifiable>({
  modifications,
  pageUrl,
}: UseModificationsFilterParams<T>) => {
  const [selectedFilter, setSelectedFilter] = useState<Filters>('all');
  const [searchValue, setSearchValue] = useState('');
  const [filteredModifications, setFilteredModifications] =
    useState<Modifiable[]>(modifications);
  useEffect(() => {
    const filterByPage =
      selectedFilter === 'all'
        ? modifications
        : modifications.filter((mod) => mod.content.pages.includes(pageUrl));
    setFilteredModifications(
      filterModificationsByText(
        filterByPage as unknown as Modifiable[],
        searchValue
      )
    );
  }, [searchValue, modifications, selectedFilter, pageUrl]);

  return {
    result: filteredModifications,
    searchValue,
    onSearchChange: setSearchValue,
    selectedFilter,
    onFiltersChange: setSelectedFilter,
  };
};
